import React, { useState, useEffect, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  VisibilityState,
  ColumnOrderState,
  ColumnSizingState,
  ColumnFiltersState,
} from "@tanstack/react-table";
import {
  ChevronDown,
  ArrowUp,
  ArrowDown,
  Filter,
  ChevronLeft,
  ChevronRight,
  Rows,
  Edit,
  Send,
  Trash2,
  Search,
  Settings,
} from "lucide-react";
import ColumnConfig from "./ColumnConfig";
import { CustomSelect } from "./CustomSelect";

interface Props<T> {
  data: T[];
  columns: ColumnDef<T>[];
  rowCount: number;
  tableName: string;
  onStateChange: (state: {
    sorting: SortingState;
    pagination: any;
    columnFilters: ColumnFiltersState;
  }) => void;
  actions?: TableAction[];
  getRowClassName?: (row: T) => string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  showPagination?: boolean;
  showPageSizeSelector?: boolean;
  defaultPageSize?: number;
  headerStats?: React.ReactNode;
  renderHeaderStats?: (selectedData: T[]) => React.ReactNode;
  initialColumnFilters?: ColumnFiltersState;
}

export interface TableAction {
  label: string;
  icon: React.ReactNode;
  onClick: (selectedRows: any[]) => void;
  disabled?: boolean;
}

export const TanStackDynamicTable = <T,>({
  data,
  columns,
  rowCount,
  tableName,
  onStateChange,
  actions,
  getRowClassName,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  showPagination = true,
  showPageSizeSelector = true,
  defaultPageSize = 20,
  renderHeaderStats,
  initialColumnFilters,
}: Props<T>) => {
  const [rowSelection, setRowSelection] = useState({});
  const [configState, setConfigState] = useState<{
    isOpen: boolean;
    targetId: string | null;
  }>({
    isOpen: false,
    targetId: null,
  });
  const [sorting, setSorting] = useState<SortingState>(
    JSON.parse(localStorage.getItem(`${tableName}_sorting`) || "[]"),
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    initialColumnFilters ?? JSON.parse(localStorage.getItem(`${tableName}_filters`) || "[]"),
  );

  // Sync when parent pushes new filters (e.g. URL navigation)
  const initialFiltersJson = JSON.stringify(initialColumnFilters);
  useEffect(() => {
    if (initialColumnFilters) {
      setColumnFilters(initialColumnFilters);
    }
  }, [initialFiltersJson]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    JSON.parse(localStorage.getItem(`${tableName}_visibility`) || "{}"),
  );
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() => {
    const saved = localStorage.getItem(`${tableName}_order`);
    return saved ? JSON.parse(saved) : (columns.map((c) => c.id) as string[]);
  });
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(
    JSON.parse(localStorage.getItem(`${tableName}_sizing`) || "{}"),
  );
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: defaultPageSize });

  const table = useReactTable({
    data,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      sorting,
      columnVisibility,
      columnOrder,
      columnSizing,
      pagination,
      columnFilters,
      rowSelection,
    },
    // Handler for Sorting
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      setSorting(next);
      onStateChange({
        sorting: next,
        pagination,
        columnFilters,
      });
    },
    // Handler for Pagination
    onPaginationChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(pagination) : updater;
      setPagination(next);
      onStateChange({
        sorting,
        pagination: next,
        columnFilters,
      });
    },
    // Handler for Column Filters
    onColumnFiltersChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(columnFilters) : updater;
      setColumnFilters(next);
      const resetPagination = { ...pagination, pageIndex: 0 };
      setPagination(resetPagination);
      onStateChange({
        sorting,
        pagination: resetPagination,
        columnFilters: next,
      });
    },
    // Core Logic
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),

    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getRowId: (row: any, relIdx) => row.id.toString() ?? relIdx.toString(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true, // Tell TanStack we handle filtering on the backend
    columnResizeMode: "onChange",
  });

  const selectedRows = table.getSelectedRowModel().rows;
  const selectedCount = selectedRows.length;
  const selectedData = table.getSelectedRowModel().rows.map((r) => r.original);

  const selectedStats = useMemo(() => {
    return selectedRows.reduce(
      (acc, row) => {
        // Cast to any or your TicketDetail type to bypass the generic check
        const item = row.original as any;

        const qty = Number(item.receivedQty) || 0;
        const isDup = item.duplicatedTicketNumber === true;

        return {
          totalQty: acc.totalQty + qty,
          dupCount: acc.dupCount + (isDup ? 1 : 0),
          dupQty: acc.dupQty + (isDup ? qty : 0),
        };
      },
      { totalQty: 0, dupCount: 0, dupQty: 0 },
    );
  }, [selectedRows]);

  // Sync LocalStorage
  useEffect(() => {
    localStorage.setItem(
      `${tableName}_visibility`,
      JSON.stringify(columnVisibility),
    );
    localStorage.setItem(`${tableName}_order`, JSON.stringify(columnOrder));
    localStorage.setItem(`${tableName}_sizing`, JSON.stringify(columnSizing));
    localStorage.setItem(`${tableName}_filters`, JSON.stringify(columnFilters));
    localStorage.setItem(`${tableName}_sorting`, JSON.stringify(sorting));
  }, [
    columnVisibility,
    columnOrder,
    columnSizing,
    columnFilters,
    sorting,
    tableName,
  ]);

  return (
    <div className="flex flex-col h-full w-full bg-white dark:bg-slate-900 overflow-hidden border border-slate-200 dark:border-slate-800 rounded-xl">
      {/* TOP BAR: Stats and View Config */}
      <div className="flex items-center justify-between px-6 py-2 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 z-20">
        {/* Left Side: Stats and Actions */}
        <div className="flex items-center gap-4">
          {renderHeaderStats && renderHeaderStats(selectedData)}

          <div className="flex items-center gap-1 pl-2">
            {actions?.map((action, index) => (
              <button
                key={index}
                // disabled={selectedCount === 0 || action.disabled}
                onClick={() =>
                  action.onClick(selectedRows.map((r) => r.original))
                }
                className={`flex items-center gap-2 px-3 py-2 text-sm font-bold rounded-lg transition-colors disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200`}
              >
                {action.icon}
                {action.label}
              </button>
            ))}
          </div>
        </div>

        {/* Right Side: Search and Filter Configuration */}
        <div className="flex items-center gap-3">
          {/* Render Search Bar ONLY if onSearchChange is provided */}
          {onSearchChange && (
            <div className="relative w-64">
              <input
                type="text"
                placeholder={searchPlaceholder || "Search..."}
                value={searchValue || ""}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all text-slate-700 dark:text-slate-200"
              />
              <Search
                className="absolute left-3 top-2.5 text-slate-400"
                size={16}
              />
            </div>
          )}

          <button
            onClick={() => setConfigState({ isOpen: true, targetId: null })}
            className="flex items-center gap-2 px-4 py-2 text-sm font-bold bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-xl transition-colors text-slate-700 dark:text-slate-200"
          >
            <Filter size={16} />
          </button>
        </div>
      </div>

      {/* 2. SCROLLABLE CONTENT: The actual table */}
      <div className="flex-1 overflow-auto custom-scrollbar relative">
        <table
          className="w-full border-collapse"
          style={{ width: table.getTotalSize() }}
        >
          <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900 shadow-sm">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  // Logic to determine if filter is active for this specific column
                  const filterValue = header.column.getFilterValue();
                  const isFilterActive = Array.isArray(filterValue)
                    ? filterValue.some((f: any) => f.value !== "")
                    : filterValue && (filterValue as any).value !== "";

                  return (
                    <th
                      key={header.id}
                      style={{ width: header.getSize() }}
                      className="relative h-12 px-4 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b border-r border-slate-200 dark:border-slate-800 group"
                    >
                      <div
                        className="flex items-center gap-2 cursor-pointer select-none group"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <span className={isFilterActive ? "text-primary" : ""}>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {!!isFilterActive && "*"}
                        </span>

                        <span className="flex-shrink-0 w-4">
                          {{
                            asc: <ArrowUp size={14} className="text-primary" />,
                            desc: (
                              <ArrowDown size={14} className="text-primary" />
                            ),
                          }[header.column.getIsSorted() as string] ?? null}
                        </span>
                      </div>

                      {header.column.id !== "select" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfigState({
                              isOpen: true,
                              targetId: header.column.id,
                            });
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-all text-slate-400 hover:text-primary mr-1"
                          title="Configure this column"
                        >
                          <Settings size={14} />
                        </button>
                      )}

                      {/* Resize Handle */}
                      <div
                        onMouseDown={header.getResizeHandler()}
                        onBlur={header.getResizeHandler()}
                        className={`absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary transition-colors ${
                          header.column.getIsResizing() ? "bg-primary" : ""
                        }`}
                      />
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, rowIndex) => {
              const baseRowClasses =
                "border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200";
              const stripeClasses = rowIndex % 2 === 1
                ? "bg-slate-100/70 dark:bg-slate-800/50"
                : "";
              const hoverClasses = "hover:bg-slate-100/70 dark:hover:bg-slate-800/50";
              const customClasses = getRowClassName
                ? getRowClassName(row.original)
                : "";

              return (
                <tr
                  key={row.id}
                  className={`h-14 transition-colors border-b ${baseRowClasses} ${stripeClasses} ${hoverClasses} ${customClasses}`}
                >
                  {row.getVisibleCells().map((cell) => (
                    // Notice: Text color classes are removed here so they inherit from the <tr>
                    <td key={cell.id} className="px-4 text-sm font-medium">
                      {
                        flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        ) as React.ReactNode
                      }
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 3. FIXED BOTTOM BAR: Pagination and Page Size Selector */}
      {showPagination && (
        <div className="flex items-center justify-between px-1 py-1 bg-slate-50/50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 z-20">
          {/* Page Size Selector */}
          {showPageSizeSelector && (
            <div className="flex items-center gap-3">
              <CustomSelect
                className="w-32"
                value={table.getState().pagination.pageSize}
                options={[10, 20, 30, 40, 50, 100].map((size) => ({
                  label: `Show ${size}`,
                  value: size,
                }))}
                onChange={(val) => table.setPageSize(Number(val))}
              />
            </div>
          )}

          {/* Navigation Controls */}
          <div className="flex items-center gap-6">
            <div className="text-sm font-bold text-slate-600 dark:text-slate-400">
              Page{" "}
              <span className="text-slate-900 dark:text-white">
                {table.getState().pagination.pageIndex + 1}
              </span>{" "}
              of {table.getPageCount()}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="p-2 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-white dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="p-2 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-white dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      )}

      {configState.isOpen && (
        <ColumnConfig
          table={table}
          onClose={() => setConfigState({ isOpen: false, targetId: null })}
          targetColumnId={configState.targetId} // Pass the ID!
        />
      )}
    </div>
  );
};
